import { FC, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

import Button from "@/components/Button";
import Text from "@/components/Text";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import { hexToRgba, scrollBarStyles } from "@/utils/theme";
import { HighlightResource } from "@/types/resources";
import {
  BreakdownType,
  getBreakdownColor,
  SelectionState,
} from "./ProductionWysiwyg";
import { Textarea } from "@/components/Forms";

const Modal = styled.div`
  width: 270px;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  position: absolute;
  padding: 24px 16px 16px;
  z-index: 2;
`;

const ActionButton = styled(Button)`
  height: 38px;
  display: flex;
  flex: 1;
  width: 100%;
  align-items: center;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
`;

const SectionHeading = styled(Text)`
  margin-bottom: 8px;
  text-transform: uppercase;
  margin-top: 16px;
`;

const SelectionText = styled(Text)`
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  margin-bottom: 16px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const ContextMenuFooter = styled.div`
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale};
  margin-top: 16px;
`;

const Highlights = styled.div`
  max-height: 232px;
  overflow-y: auto;
  ${scrollBarStyles};
`;

const HighlightButton = styled.button<{ $color: string; $active?: boolean }>`
  background: rgba(226, 159, 13, 0.1);
  border-radius: 4px;
  height: 32px;
  padding: 0 10px;
  text-align: center;
  width: 100%;
  margin-bottom: 8px;
  background-color: ${(props) => hexToRgba(props.$color, 0.1)};
  transition: opacity ease-out 0.2s;
  border: solid 1px transparent;
  transition: border-color ease-out 0.2s;
  &:last-child {
    margin-bottom: 0;
  }
  ${Text} {
    color: ${(props) => props.$color};
  }
  &:hover,
  &:focus {
    border-color: ${(props) => props.$color};
  }
  ${({ $active, $color }) =>
    $active &&
    css`
      border-color: ${$color};
    `}
`;

const ContextMenuCloseButton = styled.button`
  position: absolute;
  right: 15px;
  top: 15px;
  height: 20px;
  width: 20px;
  transition: background-color ease-out 0.2s;
  &:hover {
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
  }
`;

const BoldButton = styled(Button)`
  height: 31px;
  width: 31px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledTextarea = styled(Textarea)`
  resize: none;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  margin-bottom: 16px;
  font-size: 12px;
  line-height: 18px;
  font-weight: 400;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
`;

const SelectionButton = styled.button<{ $active: boolean }>`
  padding: 8px 14px 8px 16px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  margin-bottom: 8px;
  transition: border-color ease 0.2s;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
  svg {
    margin-left: auto;
  }
  ${({ $active }) =>
    $active &&
    css`
      border-color: rgba(34, 34, 34, 0.25);
    `}
`;

const SelectionButtonDot = styled.div<{ $background?: string }>`
  width: 6px;
  height: 6px;
  background: ${(props) =>
    props.$background
      ? props.$background
      : ({ theme }) => theme.colors.postProduction};
  border-radius: 50%;
  margin-right: 8px;
`;

// TODO: fix any
const restoreSelection = (range: any) => {
  if (range) {
    if (window.getSelection) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    } else if (document.getSelection() && range.select) {
      range.select();
    }
  }
};

interface Props {
  setSelectionState: React.Dispatch<React.SetStateAction<SelectionState>>;
  anchorPoint: {
    x: number;
    y: number;
  };
  isSelectionUpdatable: boolean;
  selectionState: SelectionState;
  replaceSelectedText: () => void;
  onSelectionRemove: () => void;
  onUpdateSelectedText: (newText: string) => void;
  onModalClose: () => void;
  selectedHighlightElement?: Element;
  selection?: Range;
  highlights?: HighlightResource[];
  onComment: (element?: Element) => void;
}

const ProductionWysiwygModal: FC<Props> = ({
  setSelectionState,
  anchorPoint,
  isSelectionUpdatable,
  selectionState,
  replaceSelectedText,
  onSelectionRemove,
  onUpdateSelectedText,
  onModalClose,
  selectedHighlightElement,
  selection,
  highlights,
  onComment,
}) => {
  const [textareaValue, setTextareaValue] = useState(
    selectedHighlightElement?.textContent?.toString() || ""
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const textAreaAdjust = () => {
    const element = textareaRef.current;
    if (!element) return;
    element!.style.height = "1px";
    element!.style.height = 2 + element!.scrollHeight + "px";
  };

  const onSaveSelection = () => {
    replaceSelectedText();
    onModalClose();
  };

  const onSelectionUpdate = () => {
    onUpdateSelectedText(textareaValue);
    onModalClose();
  };

  useEffect(() => {
    textAreaAdjust();
  }, [textareaValue]);

  const modalRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(modalRef, onModalClose);

  useEffect(() => {
    if (!selectedHighlightElement) return;

    const element = selectedHighlightElement as HTMLElement;

    setSelectionState({
      color: element.style.background,
      ...(!!element.dataset.highlight && {
        highlightId: parseInt(element.dataset.highlight),
      }),
      ...(!!element.dataset.breakdown && {
        breakdownType: element.dataset.breakdown as BreakdownType,
      }),
    });
  }, [selectedHighlightElement, setSelectionState]);

  return (
    <Modal
      ref={modalRef}
      style={{
        top: anchorPoint.y + 20,
        left: anchorPoint.x - 100,
      }}
      onClick={() => {
        restoreSelection(selection);
      }}
    >
      <ContextMenuCloseButton
        onClick={(e) => {
          e.stopPropagation();
          onModalClose();
        }}
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 1L1 9"
            stroke="#222222"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9 9L1 1"
            stroke="#222222"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </ContextMenuCloseButton>
      <SectionHeading $color="gray" $type="extraSmall">
        Selection:
      </SectionHeading>
      {!!selectedHighlightElement ? (
        <StyledTextarea
          ref={textareaRef}
          spellCheck="false"
          defaultValue={selectedHighlightElement.textContent?.toString()}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setTextareaValue(e.target.value)}
        />
      ) : (
        <SelectionText $type="smallBold">{selection?.toString()}</SelectionText>
      )}
      <SectionHeading $color="gray" $type="extraSmall">
        Actions
      </SectionHeading>
      <Actions>
        <BoldButton
          color="secondary"
          onClick={(e: KeyboardEvent) => {
            e.preventDefault();
            document.execCommand("bold", false);
            onModalClose();
          }}
        >
          <svg
            width="9"
            height="11"
            viewBox="0 0 9 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.75 10.5V0.5H4.77235C5.48929 0.5 6.0689 0.643519 6.51117 0.930556C6.95344 1.21296 7.277 1.56944 7.48184 2C7.68669 2.43056 7.78911 2.86574 7.78911 3.30556C7.78911 3.86574 7.66108 4.33102 7.40503 4.70139C7.15363 5.07176 6.8068 5.31944 6.36453 5.44444V5.09722C6.99302 5.22685 7.46322 5.5162 7.77514 5.96528C8.09171 6.41435 8.25 6.91667 8.25 7.47222C8.25 8.06944 8.13827 8.59491 7.9148 9.04861C7.696 9.50232 7.3608 9.8588 6.90922 10.1181C6.45763 10.3727 5.88966 10.5 5.20531 10.5H0.75ZM2.67737 8.72222H5.00978C5.26583 8.72222 5.49395 8.66898 5.69413 8.5625C5.89432 8.45139 6.05028 8.29861 6.16201 8.10417C6.2784 7.90509 6.33659 7.6713 6.33659 7.40278C6.33659 7.16667 6.28771 6.95602 6.18994 6.77083C6.09218 6.58565 5.9432 6.43981 5.74302 6.33333C5.54749 6.22222 5.30307 6.16667 5.00978 6.16667H2.67737V8.72222ZM2.67737 4.40278H4.74441C4.95857 4.40278 5.14944 4.36574 5.31704 4.29167C5.48464 4.21759 5.61732 4.10417 5.71508 3.95139C5.81285 3.79398 5.86173 3.59259 5.86173 3.34722C5.86173 3.04167 5.76629 2.78472 5.57542 2.57639C5.38454 2.36806 5.10754 2.26389 4.74441 2.26389H2.67737V4.40278Z"
              fill="currentColor"
            />
          </svg>
        </BoldButton>
        <BoldButton
          color="secondary"
          onClick={() => onComment(selectedHighlightElement)}
        >
          <svg
            width="17"
            height="17"
            viewBox="0 0 17 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.60205 11.9997C1.7501 10.5635 1.45174 8.86571 1.76299 7.22511C2.07425 5.5845 2.97371 4.11395 4.29248 3.08959C5.61125 2.06524 7.25861 1.55753 8.92522 1.66183C10.5918 1.76612 12.163 2.47523 13.3438 3.656C14.5246 4.83678 15.2337 6.40799 15.338 8.0746C15.4423 9.74121 14.9346 11.3886 13.9103 12.7073C12.8859 14.0261 11.4154 14.9256 9.77478 15.2369C8.13417 15.5481 6.43637 15.2498 5.00018 14.3978L5.00019 14.3977L2.63187 15.0744C2.53388 15.1024 2.43019 15.1037 2.33154 15.0781C2.23289 15.0526 2.14288 15.0011 2.07082 14.929C1.99876 14.857 1.94728 14.767 1.92172 14.6683C1.89616 14.5697 1.89744 14.466 1.92544 14.368L2.6021 11.9997L2.60205 11.9997Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6.21429 7.35742H10.7857"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6.21429 9.64258H10.7857"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </BoldButton>
      </Actions>
      {!!highlights?.length && (
        <>
          <SectionHeading $color="gray" $type="extraSmall">
            Select highlight:
          </SectionHeading>
          <Highlights>
            {highlights.map((highlight) => (
              <HighlightButton
                key={highlight.id}
                $color={highlight.color}
                $active={selectionState?.highlightId === highlight.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectionState({
                    color: hexToRgba(highlight.color, 0.15),
                    highlightId: highlight.id,
                  });
                }}
              >
                <Text $type="smallBold">{highlight.title}</Text>
              </HighlightButton>
            ))}
          </Highlights>
        </>
      )}
      <SectionHeading $color="gray" $type="extraSmall">
        Categories
      </SectionHeading>
      <SelectionButton
        onClick={() =>
          setSelectionState({
            breakdownType: BreakdownType.LOCATION,
            color: hexToRgba(getBreakdownColor(BreakdownType.LOCATION), 0.1),
          })
        }
        $active={selectionState?.breakdownType === BreakdownType.LOCATION}
      >
        <SelectionButtonDot
          $background={getBreakdownColor(BreakdownType.LOCATION)}
        />
        <Text $type="smallBold">Locations</Text>
      </SelectionButton>
      <SelectionButton
        onClick={() =>
          setSelectionState({
            breakdownType: BreakdownType.PROP,
            color: hexToRgba(getBreakdownColor(BreakdownType.PROP), 0.1),
          })
        }
        $active={selectionState?.breakdownType === BreakdownType.PROP}
      >
        <SelectionButtonDot
          $background={getBreakdownColor(BreakdownType.PROP)}
        />
        <Text $type="smallBold">Props</Text>
      </SelectionButton>
      <SelectionButton
        onClick={() =>
          setSelectionState({
            breakdownType: BreakdownType.SHOPPING_ITEM,
            color: hexToRgba(
              getBreakdownColor(BreakdownType.SHOPPING_ITEM),
              0.1
            ),
          })
        }
        $active={selectionState?.breakdownType === BreakdownType.SHOPPING_ITEM}
      >
        <SelectionButtonDot
          $background={getBreakdownColor(BreakdownType.SHOPPING_ITEM)}
        />
        <Text $type="smallBold">Shopping List</Text>
      </SelectionButton>
      <SelectionButton
        onClick={() =>
          setSelectionState({
            breakdownType: BreakdownType.ACTOR,
            color: hexToRgba(getBreakdownColor(BreakdownType.ACTOR), 0.1),
          })
        }
        $active={selectionState?.breakdownType === BreakdownType.ACTOR}
      >
        <SelectionButtonDot
          $background={getBreakdownColor(BreakdownType.ACTOR)}
        />
        <Text $type="smallBold">Actors & Crafters</Text>
      </SelectionButton>
      <SelectionButton
        $active={selectionState?.breakdownType === BreakdownType.VOICEOVER}
        onClick={() =>
          setSelectionState({
            breakdownType: BreakdownType.VOICEOVER,
            color: hexToRgba(getBreakdownColor(BreakdownType.VOICEOVER), 0.1),
          })
        }
      >
        <SelectionButtonDot
          $background={getBreakdownColor(BreakdownType.VOICEOVER)}
        />
        <Text $type="smallBold">Voiceover</Text>
      </SelectionButton>
      <ContextMenuFooter>
        {!isSelectionUpdatable ? (
          <ActionButton
            disabled={!selectionState?.color}
            small
            onClick={onSaveSelection}
          >
            Save Selection
          </ActionButton>
        ) : (
          <ActionButtons>
            {!(selectedHighlightElement as HTMLElement)?.dataset?.breakdown && (
              <ActionButton
                small
                disabled={!textareaValue.length}
                onClick={onSelectionUpdate}
              >
                Update
              </ActionButton>
            )}
            <ActionButton
              color="secondary"
              small
              onClick={() => {
                onSelectionRemove();
                onModalClose();
              }}
            >
              Remove
            </ActionButton>
          </ActionButtons>
        )}
      </ContextMenuFooter>
    </Modal>
  );
};

export default ProductionWysiwygModal;
